import { AlertTriangle, Info } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/useI18n'
import { useSaasSubscription } from '../hooks/useSaasSubscription'
import { formatBillingMonth } from '../lib/billingMonth'

/**
 * SaaS 化の告知と承認のお願い (#1689)。
 *
 * 銀行アプリの確認事項と同じ形 —「開くと『仕様が変わったので承認をお願いします』が
 * 出る」。**押すまで出続けるが、画面は塞がない。**
 *
 * 塞がない理由は #1683 の休会バナーと同じで、塞ぐと申込みにもカード登録にも
 * たどり着けなくなるため。オーナーにやってほしいことが画面の中にある以上、
 * その画面を覆ってはいけない。
 *
 * 段階は期限との距離で強める:
 *   consent  … 承認とカード登録のお願い
 *   warning  … 残り日数を出す
 *   overdue  … 期限を過ぎた (停止までの残り日数を出す)
 *   unpaid   … 申込み済みだが引き落とせなかった (同上)
 */
export default function StoreSaasNoticeBanner() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useSaasSubscription()

  const stage = state?.notice.stage ?? 'none'
  if (stage === 'none') return null
  // 申込み画面そのものでは出さない。同じ内容が二重に並ぶだけで、操作の邪魔になる。
  if (location.pathname === '/store/subscription') return null

  const urgent = stage === 'warning' || stage === 'overdue' || stage === 'unpaid'
  const daysLeft = state?.notice.daysLeft
  const daysUntilSuspend = state?.notice.daysUntilSuspend
  const failedMonth = formatBillingMonth(state?.notice.failedMonth)
  const fee = state?.offer.monthlyFeeJpy ?? 0
  const startsOn = state?.offer.billingStartAt
    ? new Date(state.offer.billingStartAt).toLocaleDateString('ja-JP')
    : null

  const headline =
    stage === 'unpaid'
      ? t('saasNotice.unpaidTitle')
      : stage === 'overdue'
        ? t('saasNotice.overdueTitle')
        : stage === 'warning'
          ? t('saasNotice.warningTitle', { days: String(daysLeft ?? 0) })
          : t('saasNotice.consentTitle')

  return (
    <div
      className={`border-b px-4 pb-3 ${
        urgent
          ? 'border-yokoso-red/20 bg-yokoso-red/10'
          : 'border-gold/30 bg-gold/10'
      }`}
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
    >
      <div className="mx-auto flex max-w-3xl items-start gap-2">
        {urgent
          ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yokoso-red" />
          : <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />}
        <div className="min-w-0 flex-1">
          <p className={`font-body text-[13px] font-bold ${urgent ? 'text-yokoso-red' : 'text-text-primary'}`}>
            {headline}
          </p>
          <p className="mt-0.5 font-body text-[12px] text-text-secondary">
            {/* 期限を過ぎた後に「それまでにお申し込みを」と言うと日本語が破綻する。
              * 段階ごとに言うことが違うので、文言を使い回さない。 */}
            {/* 決済が通らなかった店に「お申し込みください」と出すのは筋違い。
              * **やることはカードを直すこと**なので、そう言う。 */}
            {stage === 'unpaid'
              ? t('saasNotice.unpaidLead', { month: failedMonth })
              : stage === 'overdue'
              ? t('saasNotice.overdueLead', { fee: fee.toLocaleString(), date: startsOn ?? '' })
              : startsOn
                ? t('saasNotice.lead', { fee: fee.toLocaleString(), date: startsOn })
                : t('saasNotice.leadNoDate', { fee: fee.toLocaleString() })}
          </p>
          {/* **あと何日で止まるかを具体的に出す。**「停止する場合があります」だけでは
              店は動かない。日付が見えて初めて自分の予定に入る。

              未払い (unpaid) にも出す。**実際に止まるのは主にこちら** — 申し込んだ
              うえでカードが通らなかった店で、日数が見えないまま猶予が過ぎるのは
              規約 11.5 条 1 項の催告として成り立たない。催告メールと同じ日付になる
              (どちらも最初の失敗を起点にしている)。 */}
          {(stage === 'overdue' || stage === 'unpaid') && daysUntilSuspend != null && (
            <p className="mt-0.5 font-body text-[12px] font-bold text-yokoso-red">
              {daysUntilSuspend > 0
                ? t('saasNotice.suspendIn', { days: String(daysUntilSuspend) })
                : t('saasNotice.suspendImminent')}
            </p>
          )}
          {/* **カードを先に登録してもらう。** 課金開始日までは引き落とされないことを
              明示しないと、「今すぐ金を取られる」と誤解して誰も登録しない。 */}
          {/* **未払いのときは出さない。** 「開始日までは引き落とされません」は
            * 申込み前の案内であって、既に請求が失敗している店に言うことではない
            * (「まだ猶予がある」と誤解させる)。 */}
          {stage !== 'unpaid' && !state?.hasPaymentMethod && (
            <p className="mt-0.5 font-body text-[12px] text-text-secondary">
              {t('saasNotice.cardFirst')}
            </p>
          )}
          {/* **払える画面へ送る。** お支払い方法の画面にはカード登録しか無く、
            * 直しても次のバッチを待つことになる — この機能が防ごうとした事態そのもの。 */}
          <button
            onClick={() => navigate('/store/subscription')}
            className="tap-effect mt-2 rounded-lg bg-navy px-3 py-1.5 font-body text-[12px] font-medium text-white"
          >
            {stage === 'unpaid' ? t('saasNotice.fixCard') : t('saasNotice.cta')}
          </button>
        </div>
      </div>
    </div>
  )
}
